import React, { useState } from 'react';
import { CheckCircle } from '@mui/icons-material';
import { CircularProgress, Modal, Stack, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { FailedPinnedPresetResult } from '../../../API';
import { useLocales } from '../../../hooks';
import Button from '../../shared/Button';
import { pinAllDialogTestIds } from '../../shared/TestsIds';

type PinAllDialogStatus = 'idle' | 'loading' | 'success' | 'error';

export type PinAllDialogProps = {
  isPinned: boolean;
  presetCount: number;
  onConfirm: () => Promise<FailedPinnedPresetResult[]>;
  onClose: () => void;
};

const SUCCESS_AUTOCLOSE_MS = 1200;

const useStyles = makeStyles()((theme) => ({
  body: {
    width: '31vw',
    minWidth: 400,
    maxHeight: '70vh',
    minHeight: 250,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[15]
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
    textAlign: 'center',
    fontSize: '1.2em',
    padding: theme.spacing(4),
    overflowY: 'auto'
  },
  failures: {
    width: '100%',
    textAlign: 'left',
    fontSize: '0.85rem'
  },
  failureReason: {
    color: theme.palette.text.secondary
  },
  success: {
    color: theme.palette.success.main,
    fontSize: '3rem'
  },
  footer: {
    padding: theme.spacing(4),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    display: 'flex',
    justifyContent: 'space-between',
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius
  }
}));

export function PinAllDialog({ isPinned, presetCount, onConfirm, onClose }: PinAllDialogProps): React.ReactElement {
  const { classes } = useStyles();
  const { t } = useLocales();
  const [status, setStatus] = useState<PinAllDialogStatus>('idle');
  const [failures, setFailures] = useState<FailedPinnedPresetResult[]>([]);

  const handleConfirm = async () => {
    setStatus('loading');

    try {
      const failed = await onConfirm();

      if (failed.length) {
        setFailures(failed);
        setStatus('error');
        return;
      }

      setStatus('success');
      setTimeout(onClose, SUCCESS_AUTOCLOSE_MS);
    } catch (error) {
      // A whole-request failure has no per-preset detail, so surface the message on its own
      setFailures([{ id: '', name: '', reason: (error as Error).message }]);
      setStatus('error');
    }
  };

  return (
    <Modal open onClose={status === 'loading' ? undefined : onClose}>
      <div className={classes.body} data-testid={pinAllDialogTestIds.root}>
        <div className={classes.content}>
          {status === 'idle' && (
            <Typography>
              {t(isPinned ? 'hpc.pin_all_confirm' : 'hpc.unpin_all_confirm', { count: presetCount })}
            </Typography>
          )}

          {status === 'loading' && (
            <>
              <CircularProgress data-testid={pinAllDialogTestIds.spinner} />
              <Typography>{t('hpc.pin_all_in_progress')}</Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className={classes.success} data-testid={pinAllDialogTestIds.successIcon} />
              <Typography>{t('hpc.pin_all_done')}</Typography>
            </>
          )}

          {status === 'error' && (
            <Stack gap={1} className={classes.failures} data-testid={pinAllDialogTestIds.failures}>
              <Typography>{t('hpc.pin_all_failed_title')}</Typography>
              {failures.map(({ id, name, reason }, index) => (
                <div key={id || index}>
                  {!!name && <strong>{name}</strong>}
                  <div className={classes.failureReason}>{reason}</div>
                </div>
              ))}
            </Stack>
          )}
        </div>

        <div className={classes.footer}>
          <Button color="grey" onClick={onClose} disabled={status === 'loading'}>
            {t(status === 'idle' ? 'general.cancel' : 'general.close')}
          </Button>
          {status !== 'success' && (
            <Button
              color="primary"
              onClick={handleConfirm}
              disabled={status === 'loading'}
              data-testid={pinAllDialogTestIds.confirm}
            >
              {t(status === 'error' ? 'hpc.pin_all_retry' : isPinned ? 'hpc.pin_all' : 'hpc.unpin_all')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default PinAllDialog;
